import React, { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import { getStorage } from '../utils/storage';
import './FbsOrg.css';

export function FbsOrgLanding({ template, onNavigateToFeedback, categoryRatings, setCategoryRatings, categoryIssues, setCategoryIssues }) {
    // Use passed state from parent component instead of local state
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };
        
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);
    
    useEffect(() => {
        // Initialize ratings only if they haven't been set already (preserve state on navigation)
        if (template && template.content && template.content.categories && Object.keys(categoryRatings).length === 0) {
            const initialRatings = {};
            template.content.categories.forEach(category => {
                initialRatings[category.name] = 0;
            });
            setCategoryRatings(initialRatings);
        }
    }, [template, categoryRatings, setCategoryRatings]);
    
    // Theme logic - apply organization-specific themes
    useEffect(() => {
        if (template && template.org) {
            /* ===== CUSTOM THEME ====== */
            /* Org-specific processing */
            let orgNameString = ((template.org.name || '') + " " + (template.org.shortname || '')).toLowerCase();
            console.log("orgNameString", orgNameString);
            /* Higher order of precedence downwards; DO NOT USE `else if` */
            if (orgNameString.includes('bial')) { document.getElementById("app-container-global").classList.add('theme-custom-bial'); console.log("Setting theme: bial"); }
            if (orgNameString.includes('encalm')) { document.getElementById("app-container-global").classList.add('theme-custom-encalm-light'); console.log("Setting theme: Encalm.light"); }
            if (orgNameString.includes('mial')) { document.getElementById("app-container-global").classList.add('theme-custom-violet', 'theme-custom-mial'); console.log("Setting theme: MIAL"); }
            if (orgNameString.includes('vesta')) { // Dev Testing (Theme testing)
                //document.getElementById("app-container-global").classList.add('theme-custom-encalm-light');
            }
            /* ===== / CUSTOM THEME ====== */
        }
    }, [template]);
    
    const handleRating = (categoryName, rating) => {
        setCategoryRatings(prev => ({
            ...prev,
            [categoryName]: rating
        }));
        
        // Reset issues when rating changes
        setCategoryIssues(prev => ({
            ...prev,
            [categoryName]: []
        }));
    };
    
    const toggleIssue = (categoryName, issue) => {
        setCategoryIssues(prev => {
            const currentIssues = prev[categoryName] || [];
            // Normalize issue to its name for consistent comparison
            const issueName = typeof issue === 'object' ? issue.name : issue;
            
            // Check if the issue is already selected by comparing normalized names
            const issueExists = currentIssues.some(i => {
                const existingIssueName = typeof i === 'object' ? i.name : i;
                return existingIssueName === issueName;
            });
            
            return {
                ...prev,
                [categoryName]: issueExists
                    ? currentIssues.filter(i => {
                        const existingIssueName = typeof i === 'object' ? i.name : i;
                        return existingIssueName !== issueName;
                    })
                    : [...currentIssues, issue]
            };
        });
    };
    
    const isFormValid = () => {
        // Check if all categories have been rated
        return template.content.categories.every(category => 
            categoryRatings[category.name] > 0
        );
    };
    
    const calculateAverageRating = () => {
        const ratings = Object.values(categoryRatings);
        if (ratings.length === 0) return 0;
        
        const sum = ratings.reduce((acc, rating) => acc + rating, 0);
        return sum / ratings.length;
    };
    
    const handleNext = () => {
        const averageRating = calculateAverageRating();
        
        // Create a new structure with ratings and issues combined per category
        const categoriesData = {};
        
        // Process each category to include both rating and issues
        Object.keys(categoryRatings).forEach(categoryName => {
            categoriesData[categoryName] = {
                name: categoryName, // Add the name field required by API
                rating: categoryRatings[categoryName],
                issues: categoryIssues[categoryName] || []
            };
        });
        
        // Store the data for the feedback page
        const feedbackData = {
            rating: averageRating,
            categories: categoriesData
        };
        
        // Use navigation function to go to feedback page
        if (typeof onNavigateToFeedback === 'function') {
            onNavigateToFeedback(feedbackData);
        } else {
            console.warn('onNavigateToFeedback function not provided to FbsOrgLanding');
        }
    };
    
    const renderStars = (categoryName) => {
        const maxRating = 5;
        const currentRating = categoryRatings[categoryName] || 0;
        
        return (
            <div className="stars-container">
                {[...Array(maxRating)].map((_, index) => {
                    const starValue = index + 1;
                    return (
                        <span
                            key={index}
                            className={`star-rating ${starValue <= currentRating ? 'filled' : ''}`}
                            onClick={() => handleRating(categoryName, starValue)}
                        >
                            <Icon icon="mdi:star" color={starValue <= currentRating ? '#ffd700' : '#ccc'} />
                        </span>
                    );
                })}
            </div>
        );
    };
    
    const renderIssues = (category) => {
        const rating = categoryRatings[category.name] || 0;
        const issues = category.issues || [];
        const threshold = template?.meta?.threshold || 3;
        const showIssues = rating > 0 && rating <= threshold && issues && issues.length > 0;
        
        // Return the issues container with conditional class for animation
        return (
            <div className={`category-issues ${!showIssues ? 'hidden' : ''}`}>
                {showIssues && (
                    <>
                        <h5>What issues did you experience?</h5>
                        <div className="issues-grid">
                            {issues.map((issue, index) => {
                                // Handle both string issues and object issues
                                const issueName = typeof issue === 'object' ? issue.name : issue;
                                const issueIcon = typeof issue === 'object' ? issue.icon : null;
                                // Check if issue is selected by comparing normalized names
                                const isChecked = categoryIssues[category.name]?.some(i => {
                                    const existingIssueName = typeof i === 'object' ? i.name : i;
                                    return existingIssueName === issueName;
                                }) || false;
                                
                                return (
                                    <label key={index} className="issue-checkbox-container">
                                        <input
                                            type="checkbox"
                                            className="issue-checkbox"
                                            checked={isChecked}
                                            onChange={() => toggleIssue(category.name, issue)}
                                        />
                                        <span className="issue-checkmark"></span>
                                        <div className="issue-content">
                                            {issueIcon && (
                                                <Icon 
                                                    icon={issueIcon} 
                                                    width={isMobile ? 16 : 20} 
                                                    height={isMobile ? 16 : 20} 
                                                    className="issue-icon" 
                                                />
                                            )}
                                            <span className="issue-label">{issueName}</span>
                                        </div>
                                    </label>
                                );
                            })}
                        </div>
                    </>
                )}
            </div>
        );
    };
    
    // If template is not loaded yet
    if (!template || !template.content || !template.content.categories) {
        return <div>Loading...</div>;
    }
    
    const categories = template.content.categories;
    
    return (
        <div className="org-ratings-container">
            <div className="org-rating-header">
                <h3>Please rate our services</h3>
            </div>
            
            <div className="categories-container">
                {categories.map((category, index) => (
                    <div key={index} className="category-card">
                        <div className="category-wrapper">
                            <div className="category-name-container">
                                {category.icon && (
                                    <Icon 
                                        icon={category.icon} 
                                        width={isMobile ? 24 : 32} 
                                        height={isMobile ? 24 : 32} 
                                        className="category-icon" 
                                    />
                                )}
                                <h4 className="category-name">{category.name}</h4>
                            </div>
                            {renderStars(category.name)}
                        </div>
                        {renderIssues(category)}
                    </div>
                ))}
            </div>
            
            <div className="navigation-buttons">
                <button 
                    className="next-button" 
                    onClick={handleNext} 
                    disabled={!isFormValid()}
                >
                    Next
                </button>
            </div>
            

        </div>
    );
}

// RenderHeader component for organizational feedback
export const RenderHeader = (props) => {
    const logoToUse = props.logo ?? getStorage('u4')?.hotel?.logo_img ?? null;
    return (
        <div id="header">
            <div>
                {/* LHS: Empty*/}
            </div>
            <div id="logo">
                {logoToUse ?
                    <img src={logoToUse} alt="" />
                    : <></>}
            </div>
            <div>
                {/* RHS */}
            </div>
        </div>
    );
}
